import React, { useState, useEffect } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import { supabase } from "@/services/supabase";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProfileWalletQuery } from "@/hooks/queries/useProfileWalletQuery";

import { Share2 } from "lucide-react";
import { DISPLAY_NAME_MAX_LENGTH } from "@/config/publicConfig";

export default function ProfilePage() {
  const { t } = useTranslation();
  const { profile: storeProfile, fetchProfile, user } = useAuthStore();
  const { data: pw, refetch: refetchProfileWallet } = useProfileWalletQuery(
    user?.id
  );
  const profile = pw?.profile ?? storeProfile;
  const [loading, setLoading] = useState(false);
  // const [bindEmail, setBindEmail] = useState("");
  // const [emailChangeOtp, setEmailChangeOtp] = useState("");
  // const [pendingEmailForChange, setPendingEmailForChange] = useState<
  //   string | null
  // >(null);
  const mbtiOptions = [
    "",
    "INTJ",
    "INTP",
    "ENTJ",
    "ENTP",
    "INFJ",
    "INFP",
    "ENFJ",
    "ENFP",
    "ISTJ",
    "ISFJ",
    "ESTJ",
    "ESFJ",
    "ISTP",
    "ISFP",
    "ESTP",
    "ESFP",
  ];
  const [formData, setFormData] = useState({
    display_name: "",
    role: "parent",
    bio: "",
    location: "",
    height: "",
    weight: "",
    marital_status: "未婚",
    education: "本科",
    mbti: "",
    hometown: "",
    residence: "",
    job_title: "",
    income: "10W-20W",
    assets_status: "有房有车",
    family_background: "",
    personality_hobbies: "",
    req_age_min: "",
    req_age_max: "",
    req_height_min: "",
    req_education: "不限",
    req_residence: "",
    req_other: "",
  });

  const [showContactUs, setShowContactUs] = useState(false);
  const [feedbackType, setFeedbackType] = useState("suggestion");
  const [feedbackContent, setFeedbackContent] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");

  const handleSubmitFeedback = async () => {
    if (!user) {
      setFeedbackError(t("auth.login_required") || "请先登录");
      return;
    }
    const content = feedbackContent.trim();
    if (!content) return;
    
    setSubmittingFeedback(true);
    setFeedbackError("");
    try {
      const { error } = await supabase.from("user_feedbacks").insert({
        user_id: user.id,
        type: feedbackType,
        content: content,
      });
      if (error) throw error;
      setFeedbackSuccess(true);
    } catch (err: any) {
      setFeedbackError(err.message || t("common.failed") || "提交失败");
    } finally {
      setSubmittingFeedback(false);
    }
  };


  useEffect(() => {
    if (profile) {
      setFormData({
        display_name: profile.display_name || "",
        role: profile.role || "parent",
        bio: profile.bio || "",
        location: profile.location || "",
        height: profile.height?.toString() || "",
        weight: profile.weight?.toString() || "",
        marital_status: profile.marital_status || "未婚",
        education: profile.education || "本科",
        mbti: profile.mbti || "",
        hometown: profile.hometown || "",
        residence: profile.residence || "",
        job_title: profile.job_title || "",
        income: profile.income || "10W-20W",
        assets_status: profile.assets_status || "有房有车",
        family_background: profile.family_background || "",
        personality_hobbies: profile.personality_hobbies || "",
        req_age_min: profile.req_age_min?.toString() || "",
        req_age_max: profile.req_age_max?.toString() || "",
        req_height_min: profile.req_height_min?.toString() || "",
        req_education: profile.req_education || "不限",
        req_residence: profile.req_residence || "",
        req_other: profile.req_other || "",
      });
    }
  }, [profile]);

  const handleShare = () => {
    if (!user) return;
    const shareUrl = `${window.location.origin}/?ref=${user.id}`;
    if (navigator.share) {
      navigator.share({
        title: t("app.title"),
        text: t("hero.description"),
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert(t("profile.link_copied") || "链接已复制到剪贴板，快去发给孩子吧！");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload: any = {
        ...formData,
        display_name: formData.display_name.slice(0, DISPLAY_NAME_MAX_LENGTH),
        mbti: formData.mbti ? formData.mbti : null,
        height: formData.height ? parseInt(formData.height) : null,
        weight: formData.weight ? parseInt(formData.weight) : null,
        req_age_min: formData.req_age_min ? parseInt(formData.req_age_min) : null,
        req_age_max: formData.req_age_max ? parseInt(formData.req_age_max) : null,
        req_height_min: formData.req_height_min ? parseInt(formData.req_height_min) : null,
      };
      
      const { error } = await supabase
        .from("profiles")
        .update(payload)
        .eq("id", profile?.id);

      if (error) throw error;
      await refetchProfileWallet();
      await fetchProfile();
      alert(t("profile.save_success"));
    } catch (error: any) {
      if (error.message?.includes("含有违规的联系方式信息")) {
        alert(t("profile.save_failed_contact"));
      } else {
        alert(t("common.failed") + ": " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  /*
  const handleSendEmailChangeCode = async () => {
    const email = bindEmail.trim();
    if (!email) return;
    if (email.toLowerCase() === (user?.email || "").toLowerCase()) {
      alert(t("profile.email_same_error") || "新邮箱与当前邮箱相同");
      return;
    }
    try {
      const { error } = await supabase.auth.updateUser({ email });
      if (error) throw error;
      setPendingEmailForChange(email);
      setEmailChangeOtp("");
      alert(t("profile.magic_link_sent_alert") || `验证码已发送至 ${email}`);
    } catch (error: any) {
      alert(t("common.failed") + ": " + (error?.message || "未知错误"));
    }
  };

  const handleConfirmEmailChange = async () => {
    const target = pendingEmailForChange;
    const token = emailChangeOtp.trim();
    if (!target || !token) return;
    try {
      const { error } = await supabase.auth.verifyOtp({
        email: target,
        token,
        type: "email_change",
      });
      if (error) throw error;
      setPendingEmailForChange(null);
      setBindEmail("");
      setEmailChangeOtp("");
      await fetchProfile();
      alert(t("profile.email_updated") || "邮箱已更新");
    } catch (error: any) {
      alert(t("common.failed") + ": " + (error?.message || "未知错误"));
    }
  };
  */

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6">
      <h1 className="text-2xl font-bold mb-6">{t("profile.title")}</h1>

      <div className="bg-white p-4 rounded-lg shadow space-y-3">
        <div>
          <h3 className="font-bold text-gray-800">{t("profile.account_email")}</h3>
          <p className="text-sm text-gray-500">
            {t("profile.current_email", { email: user?.email || "未设置" })}
          </p>
        </div>
        {/* 
        <div className="flex gap-2 flex-wrap">
          <input
            type="email"
            value={bindEmail}
            onChange={(e) => setBindEmail(e.target.value)}
            className="flex-1 min-w-[200px] p-2 border rounded-lg focus:ring-pink-500 focus:border-pink-500"
            placeholder={t("profile.new_email_placeholder")}
          />
          <button
            type="button"
            onClick={handleSendEmailChangeCode}
            className="px-4 py-2 bg-pink-600 text-white rounded-lg font-bold hover:bg-pink-700 disabled:opacity-50"
            disabled={!bindEmail.trim()}
          >
            {t("profile.send_code")}
          </button>
        </div>
        {pendingEmailForChange && (
          <div className="flex gap-2 mt-3 flex-wrap items-center">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={emailChangeOtp}
              onChange={(e) => setEmailChangeOtp(e.target.value)}
              className="flex-1 min-w-[120px] p-2 border rounded-lg"
              placeholder={t("profile.otp_placeholder")}
            />
            <button
              type="button"
              onClick={handleConfirmEmailChange}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg font-bold hover:bg-gray-800 disabled:opacity-50"
              disabled={!emailChangeOtp.trim()}
            >
              {t("profile.confirm_bind")}
            </button>
          </div>
        )}
        <p className="text-xs text-gray-500">
          {t("profile.bind_email_desc")}
        </p>
        */}
      </div>

      {/* Verification Status Banner */}
      <div className="bg-white p-4 rounded-lg shadow flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-800">{t("profile.age_verify_status")}</h3>
          <p
            className={`text-sm ${profile?.age_verified_status === 'approved' ? "text-green-600" : "text-red-500"}`}
          >
            {profile?.age_verified_status === 'approved'
              ? t("profile.verified")
              : profile?.age_verified_status === 'submitted'
                ? t("profile.under_review")
                : t("profile.not_verified")}
          </p>
        </div>
        {profile?.age_verified_status !== 'approved' && profile?.age_verified_status !== 'submitted' && (
          <Link
            to="/app/verify-age"
            className="px-4 py-2 bg-pink-100 text-pink-700 rounded-lg text-sm font-bold hover:bg-pink-200"
          >
            {t("profile.go_verify")}
          </Link>
        )}
      </div>

      {/* Share to Child (New Feature) */}
      <div
        className="bg-gradient-to-r from-pink-500 to-purple-600 p-4 rounded-lg shadow text-white flex items-center justify-between cursor-pointer"
        onClick={handleShare}
      >
        <div>
          <h3 className="font-bold text-lg">{t("profile.invite_child")}</h3>
          <p className="text-sm opacity-90">{t("profile.invite_child_desc")}</p>
        </div>
        <div className="bg-white/20 p-2 rounded-full">
          <Share2 size={24} />
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-6 bg-white p-6 rounded-lg shadow"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.nickname")}</label>
            <input
              type="text"
              maxLength={DISPLAY_NAME_MAX_LENGTH}
              value={formData.display_name}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  display_name: e.target.value.slice(
                    0,
                    DISPLAY_NAME_MAX_LENGTH,
                  ),
                })
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.role")}</label>
            <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border">
              <option value="parent">{t("profile.role_parent")}</option>
              <option value="child">{t("profile.role_child")}</option>
              <option value="single">{t("profile.role_single")}</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.height")}</label>
            <input type="number" value={formData.height} onChange={(e) => setFormData({ ...formData, height: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.weight")}</label>
            <input type="number" value={formData.weight} onChange={(e) => setFormData({ ...formData, weight: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border" />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.marital_status")}</label>
            <select value={formData.marital_status} onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border">
              <option value="未婚">{t("marital.single") || "未婚"}</option>
              <option value="离异未育">{t("marital.divorced_no_kids") || "离异未育"}</option>
              <option value="离异带娃">{t("marital.divorced_with_kids") || "离异带娃"}</option>
              <option value="丧偶">{t("marital.widowed") || "丧偶"}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.education")}</label>
            <select value={formData.education} onChange={(e) => setFormData({ ...formData, education: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border">
              <option value="高中及以下">{t("edu.high_school_lower") || "高中及以下"}</option>
              <option value="大专">{t("edu.junior_college") || "大专"}</option>
              <option value="本科">{t("edu.bachelor") || "本科"}</option>
              <option value="硕士">{t("edu.master") || "硕士"}</option>
              <option value="博士">{t("edu.doctor") || "博士"}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.mbti")}</label>
            <select
              value={formData.mbti}
              onChange={(e) => setFormData({ ...formData, mbti: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border"
            >
              {mbtiOptions.map((opt) => (
                <option key={opt || "unset"} value={opt}>
                  {opt || t("common.do_not_fill") || "不填写"}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.hometown")}</label>
            <input type="text" value={formData.hometown} onChange={(e) => setFormData({ ...formData, hometown: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.residence")}</label>
            <input type="text" value={formData.residence} onChange={(e) => setFormData({ ...formData, residence: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border" />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.job_title")}</label>
            <input type="text" value={formData.job_title} onChange={(e) => setFormData({ ...formData, job_title: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("profile.income")}</label>
            <select value={formData.income} onChange={(e) => setFormData({ ...formData, income: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border">
              <option value="10W以下">{t("income.below_10") || "10W以下"}</option>
              <option value="10W-20W">{t("income.10_20") || "10W-20W"}</option>
              <option value="20W-30W">{t("income.20_30") || "20W-30W"}</option>
              <option value="30W-50W">{t("income.30_50") || "30W-50W"}</option>
              <option value="50W以上">{t("income.above_50") || "50W以上"}</option>
            </select>
          </div>
          
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">{t("profile.assets")}</label>
            <select value={formData.assets_status} onChange={(e) => setFormData({ ...formData, assets_status: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border">
              <option value="有房有车">{t("assets.has_house_car") || "有房有车"}</option>
              <option value="有房无车">{t("assets.has_house_no_car") || "有房无车"}</option>
              <option value="无房有车">{t("assets.no_house_has_car") || "无房有车"}</option>
              <option value="无房无车">{t("assets.no_house_no_car") || "无房无车"}</option>
            </select>
          </div>
        </div>

        <div className="border-t pt-4">
          <h3 className="font-bold text-gray-800 mb-4">{t("profile.details_section")}</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("profile.self_intro")}</label>
              <textarea rows={3} value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border placeholder-gray-400" placeholder={t("profile.self_intro_placeholder")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("profile.family_bg")}</label>
              <textarea rows={2} value={formData.family_background} onChange={(e) => setFormData({ ...formData, family_background: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border placeholder-gray-400" placeholder={t("profile.family_bg_placeholder")} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("profile.personality_hobbies")}</label>
              <textarea rows={2} value={formData.personality_hobbies} onChange={(e) => setFormData({ ...formData, personality_hobbies: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border placeholder-gray-400" placeholder={t("profile.personality_hobbies_placeholder")} />
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <h3 className="font-bold text-gray-800 mb-4">{t("profile.partner_req")}</h3>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("profile.min_age")}</label>
              <input type="number" value={formData.req_age_min} onChange={(e) => setFormData({ ...formData, req_age_min: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 p-2 border" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("profile.max_age")}</label>
              <input type="number" value={formData.req_age_max} onChange={(e) => setFormData({ ...formData, req_age_max: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 p-2 border" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("profile.min_height")}</label>
              <input type="number" value={formData.req_height_min} onChange={(e) => setFormData({ ...formData, req_height_min: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 p-2 border" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("profile.min_edu")}</label>
              <select value={formData.req_education} onChange={(e) => setFormData({ ...formData, req_education: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 p-2 border">
                <option value="不限">{t("common.unlimited") || "不限"}</option>
                <option value="高中及以下">{t("edu.high_school_lower") || "高中及以下"}</option>
                <option value="大专">{t("edu.junior_college") || "大专"}</option>
                <option value="本科">{t("edu.bachelor") || "本科"}</option>
                <option value="硕士">{t("edu.master") || "硕士"}</option>
                <option value="博士">{t("edu.doctor") || "博士"}</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">{t("profile.target_city")}</label>
              <input type="text" value={formData.req_residence} onChange={(e) => setFormData({ ...formData, req_residence: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 p-2 border" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">{t("profile.other_req")}</label>
              <textarea rows={2} value={formData.req_other} onChange={(e) => setFormData({ ...formData, req_other: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 p-2 border" />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
        >
          {loading ? t("profile.saving") : t("profile.save_btn")}
        </button>
      </form>

      {/* Contact Us Modal */}
      {showContactUs && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md space-y-4">
            <h2 className="text-xl font-bold">{t("profile.contact_us")}</h2>
            {feedbackSuccess ? (
              <div className="text-green-600 font-medium py-8 text-center">
                {t("profile.feedback_success") || "提交成功，感谢您的反馈！"}
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("profile.feedback_type")}</label>
                  <select
                    value={feedbackType}
                    onChange={(e) => setFeedbackType(e.target.value)}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="report">{t("feedback.report") || "举报"}</option>
                    <option value="complaint">{t("feedback.complaint") || "投诉"}</option>
                    <option value="suggestion">{t("feedback.suggestion") || "意见/建议"}</option>
                    <option value="other">{t("feedback.other") || "其他"}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t("profile.feedback_content")}</label>
                  <textarea
                    rows={4}
                    value={feedbackContent}
                    onChange={(e) => setFeedbackContent(e.target.value)}
                    className="w-full p-2 border rounded-md"
                    placeholder={t("profile.feedback_placeholder")}
                  />
                </div>
                {feedbackError && <p className="text-red-500 text-sm">{feedbackError}</p>}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowContactUs(false)}
                    className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50"
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitFeedback}
                    disabled={submittingFeedback || !feedbackContent.trim()}
                    className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 disabled:opacity-50"
                  >
                    {submittingFeedback ? t("common.submitting") : t("common.send")}
                  </button>
                </div>
              </>
            )}
            {feedbackSuccess && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowContactUs(false)}
                  className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700"
                >
                  {t("common.confirm")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legal Links */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm text-gray-600">
        <h3 className="font-bold text-gray-800 mb-2">{t("profile.legal_info")}</h3>
        <button
          type="button"
          onClick={() => {
            setShowContactUs(true);
            setFeedbackSuccess(false);
            setFeedbackContent("");
            setFeedbackError("");
          }}
          className="block hover:text-pink-600 text-left w-full cursor-pointer"
        >
          {t("profile.contact_us")} ({t("feedback.complaint")} / {t("feedback.suggestion")})
        </button>
        <Link to="/privacy" className="block hover:text-pink-600">
          {t("profile.privacy")}
        </Link>
        <Link to="/terms" className="block hover:text-pink-600">
          {t("profile.terms")}
        </Link>
        <div className="block cursor-pointer hover:text-pink-600">
          {t("legal.legal_act_commercial")}
        </div>
        <div className="block cursor-pointer hover:text-pink-600">
          {t("legal.legal_act_settlement")}
        </div>
      </div>
    </div>
  );
}
